import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface CancelReason {
    id: string;
    text: string;
    active: boolean;
}

export interface CancelReasonWritePayload {
    text?: string;
    active?: boolean;
}

export function useCancelReasons() {
    return useQuery({
        queryKey: ['cancel-reasons'] as const,
        queryFn: async () => {
            const res = await api.get<CancelReason[]>('/cancel-reasons');
            return res.data;
        },
        staleTime: 60_000,
    });
}

export function useCreateCancelReason() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: CancelReasonWritePayload) => {
            const res = await api.post<CancelReason>('/cancel-reasons', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['cancel-reasons'] }),
    });
}

export function useUpdateCancelReason() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: CancelReasonWritePayload }) => {
            const res = await api.patch<CancelReason>(`/cancel-reasons/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['cancel-reasons'] }),
    });
}

export function useDeleteCancelReason() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/cancel-reasons/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['cancel-reasons'] }),
    });
}
